import { GenreDropdown } from '@components/GenreDropdown';
import { CloseIcon } from '@components/Icons/CloseIcon';
import { SearchIcon } from '@components/Icons/SearchIcon';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SearchProps {
  selectedGenre?: string;
  onGenreChange?: (genre: string) => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  category?: string; // music, serenity, books, Indian music
  genres?: string[];
}

export const Search = memo(
  ({
    selectedGenre,
    onGenreChange,
    searchQuery,
    onSearchChange,
    category = 'music',
    genres,
  }: SearchProps = {}) => {
    const { t } = useTranslation();
    const inputRef = useRef<HTMLInputElement>(null);
    const [localSearchQuery, setLocalSearchQuery] = useState<string>(
      searchQuery || '',
    );

    // Sync local state with prop changes
    useEffect(() => {
      setLocalSearchQuery(searchQuery || '');
    }, [searchQuery]);

    // Handle form submission (Enter key)
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (onSearchChange) {
        const trimmedQuery = localSearchQuery.trim();
        // Always call onSearchChange, even for empty strings to clear the search
        onSearchChange(trimmedQuery);
      }
    };

    // Handle clear button click
    const handleClear = () => {
      setLocalSearchQuery('');
      if (onSearchChange) {
        onSearchChange('');
      }
    };

    // Handle input change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setLocalSearchQuery(e.target.value);
    };

    return (
      <div
        id="search"
        className="flex px-4 sm:px-6 gap-[12px] pb-6 relative items-center">
        {/* Search Input - Reduced width to make space for genre dropdown */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-1 h-[48px] bg-white/10 rounded-full px-4 py-2 pl-12 pr-12 relative">
          <input
            ref={inputRef}
            value={localSearchQuery}
            onChange={handleInputChange}
            className="border-0 w-full outline-none text-[14px] font-normal text-[#ffffff] bg-transparent"
            placeholder={t('search.placeholder')}
            name="search"
            type="text"
            autoComplete="off"
          />
          {/* Search Icon */}
          <span className="flex items-center absolute left-[16px] top-0 bottom-0">
            <SearchIcon />
          </span>

          {/* Clear Icon - only show when there's text */}
          {localSearchQuery && (
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center absolute right-[16px] top-0 bottom-0 hover:bg-white/10 rounded-full p-1 transition-colors duration-200">
              <CloseIcon width={16} height={16} fillOpacity={0.6} />
            </button>
          )}
        </form>

        {/* Genre Dropdown - Only show if props are provided */}
        {selectedGenre && onGenreChange && (
          <div className="flex-shrink-0">
            <GenreDropdown
              selectedGenre={selectedGenre}
              onGenreChange={onGenreChange}
              className="text-sm"
              category={category}
              genres={genres}
            />
          </div>
        )}
      </div>
    );
  },
);

Search.displayName = 'Search';
